import { Component, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import PublicLayout from './components/layout/PublicLayout';
import AccountLayout from './components/layout/AccountLayout';
import AdminLayout from './components/layout/AdminLayout';
import MaintenanceGuard from './components/common/MaintenanceGuard';
import { AdminRoute } from './components/common/AdminRoute';
import { ToastProvider } from './components/common/Toast';
import { WebsiteContentProvider } from './contexts/WebsiteContentContext';
import { LanguageProvider } from './contexts/LanguageContext';
import AdminOverlayToggle from './components/admin/AdminOverlayToggle';
import InlineEditModal from './components/admin/InlineEditModal';
import OpenInAppBanner from './components/common/OpenInAppBanner';
import { initErrorCapture } from './services/error-capture';

// Initialize error capture for bug reports
initErrorCapture();

// Public pages
import Landing from './pages/public/Landing';
import Login from './pages/public/Login';
import Signup from './pages/public/Signup';
import Privacy from './pages/public/Privacy';
import Terms from './pages/public/Terms';
import Credits from './pages/public/Credits';
import ForgotPassword from './pages/public/ForgotPassword';
import ResetPassword from './pages/public/ResetPassword';
import AuthCallback from './pages/public/AuthCallback';
import Deals from './pages/public/deals';

// Account pages
import Account from './pages/account/Account';
import Upgrade from './pages/account/upgrade';
import Settings from './pages/account/settings';
import Watchlist from './pages/account/watchlist';
import Checklists from './pages/account/checklists';
import Profile from './pages/account/profile';
import Notifications from './pages/account/notifications';
import Challenges from './pages/account/challenges';
import Referrals from './pages/account/referrals';
import { MyListings, CreateListing, Messages as MarketplaceMessages, Chat as MarketplaceChat } from './pages/account/marketplace';

// Public marketplace pages
import ListingDetail from './pages/marketplace/ListingDetail';

// Admin pages
import * as Admin from './pages/admin';

// Auth guard component
import { useAuthStore } from './stores/auth-store';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
    },
  },
});

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuthStore();

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

/* eslint-disable rumio-i18n/no-hardcoded-strings */
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('React Error Boundary caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '2rem', textAlign: 'center', fontFamily: 'system-ui' }}>
          <h1 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>Something went wrong</h1>
          <p style={{ color: '#666', marginBottom: '1rem' }}>{this.state.error?.message}</p>
          <button
            onClick={() => window.location.reload()}
            style={{ padding: '0.5rem 1rem', background: '#0077B6', color: 'white', border: 'none', borderRadius: '0.5rem', cursor: 'pointer' }}
          >
            Reload Page
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
/* eslint-enable rumio-i18n/no-hardcoded-strings */

function App() {
  return (
    <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <ToastProvider>
          <LanguageProvider>
            <WebsiteContentProvider>
              <MaintenanceGuard>
            {/* Admin overlay components - only render for admins */}
            <AdminOverlayToggle />
            <InlineEditModal />

            {/* Mobile app banner - shows on mobile browsers for logged-in users */}
            <OpenInAppBanner />

            <Routes>
              {/* Public routes */}
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/credits" element={<Credits />} />
                <Route path="/deals" element={<Deals />} />
                {/* Public marketplace routes */}
                <Route path="/marketplace/listing/:id" element={<ListingDetail />} />
              </Route>

              {/* Protected account routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <AccountLayout />
                  </ProtectedRoute>
                }
              >
                <Route path="/account" element={<Account />} />
                <Route path="/account/subscription" element={<Upgrade />} />
                <Route path="/account/settings" element={<Settings />} />
                <Route path="/account/watchlist" element={<Watchlist />} />
                <Route path="/account/checklists" element={<Checklists />} />
                <Route path="/account/profile" element={<Profile />} />
                <Route path="/account/notifications" element={<Notifications />} />
                <Route path="/account/challenges" element={<Challenges />} />
                <Route path="/account/referrals" element={<Referrals />} />
                {/* Marketplace routes */}
                <Route path="/account/marketplace" element={<MyListings />} />
                <Route path="/account/marketplace/new" element={<CreateListing />} />
                <Route path="/account/marketplace/edit/:id" element={<CreateListing />} />
                <Route path="/account/marketplace/messages" element={<MarketplaceMessages />} />
                <Route path="/account/marketplace/messages/:id" element={<MarketplaceChat />} />
              </Route>

              {/* Admin routes */}
              <Route
                element={
                  <AdminRoute>
                    <AdminLayout />
                  </AdminRoute>
                }
              >
                <Route path="/admin" element={<Admin.Dashboard />} />
                <Route path="/admin/ops" element={<Admin.OperationsCenter />} />
                <Route path="/admin/errors" element={<Admin.ErrorInvestigation />} />
                <Route path="/admin/users" element={<Admin.Users />} />
                <Route path="/admin/users/:userId" element={<Admin.UserDetail />} />
                <Route path="/admin/system" element={<Admin.System />} />
                <Route path="/admin/translations" element={<Admin.Translations />} />
                <Route path="/admin/bug-reports" element={<Admin.BugReports />} />
                <Route path="/admin/website" element={<Admin.Website />} />
                <Route path="/admin/settings" element={<Admin.AdminSettings />} />
                <Route path="/admin/ai-settings" element={<Admin.AISettings />} />
                <Route path="/admin/ai-agent" element={<Admin.AIAgentDashboard />} />
                <Route path="/admin/reports" element={<Admin.Reports />} />
                <Route path="/admin/user-insights" element={<Admin.UserInsights />} />
                <Route path="/admin/dev-builds" element={<Admin.DevBuilds />} />
                <Route path="/admin/seeds" element={<Admin.SeedBrowsers />} />
                <Route path="/admin/seeds/map" element={<Admin.SeedMap />} />
                <Route path="/admin/seeds/:seedId/health" element={<Admin.SeedHealthReport />} />
                <Route path="/admin/store-browser" element={<Admin.StoreDataBrowser />} />
                <Route path="/admin/store-browser/:country" element={<Admin.StoreDataBrowser />} />
                <Route path="/admin/store-browser/:country/:storeId" element={<Admin.StoreDataBrowser />} />
                {/* SMCC - Social Media Control Center */}
                <Route path="/admin/smcc" element={<Admin.SMCCDashboard />} />
                <Route path="/admin/smcc/accounts" element={<Admin.SMCCAccounts />} />
                <Route path="/admin/smcc/callback" element={<Admin.SMCCCallback />} />
                <Route path="/admin/smcc/calendar" element={<Admin.SMCCCalendar />} />
                <Route path="/admin/smcc/content" element={<Admin.SMCCContentList />} />
                <Route path="/admin/smcc/content/new" element={<Admin.SMCCContentEditor />} />
                <Route path="/admin/smcc/content/:id" element={<Admin.SMCCContentEditor />} />
                <Route path="/admin/smcc/content/:id/edit" element={<Admin.SMCCContentEditor />} />
                <Route path="/admin/smcc/inbox" element={<Admin.SMCCInbox />} />
                <Route path="/admin/smcc/analytics" element={<Admin.SMCCAnalytics />} />
                <Route path="/admin/smcc/campaigns" element={<Admin.SMCCCampaigns />} />
                <Route path="/admin/smcc/campaigns/:id" element={<Admin.SMCCCampaigns />} />
                <Route path="/admin/smcc/assets" element={<Admin.SMCCAssetLibrary />} />
                <Route path="/admin/smcc/settings" element={<Admin.SMCCSettings />} />
                {/* Slot Machine Skins */}
                <Route path="/admin/slot-skins" element={<Admin.SlotSkins />} />
                <Route path="/admin/slot-skins/:id" element={<Admin.SlotSkinEditor />} />
              </Route>

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </MaintenanceGuard>
            </WebsiteContentProvider>
          </LanguageProvider>
        </ToastProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
